"use client";

import { useEffect, useState } from "react";
import {
  Search,
  ArrowLeft,
  Users,
  TrendingUp,
  Plus,
  Eye,
  Filter,
  Grid,
  List,
  Trash2,
  Info,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  X,
  Calendar,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import Modal from "../layout/Modal";
import EmptyState from "../shared/EmptyState";
import NoFilterData from "../shared/FilterNoResults";
import NoDataFound from "../shared/NoDataFound";
import CustomTooltip from "../shared/CustomTooltip";
import Pagination from "../shared/paginator/paginator";
import "./TeamsPage.css";
import { useNavigate } from "react-router-dom";
import { getAllTeamsApi, deleteTeamApi } from "../../services/teamsService";
import { getDepartmentsListApi } from "../../services/settingsService";
import {
  formatEmployeeCreatedAt,
  handleApiError as baseHandleApiError,
} from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { useTranslation } from "react-i18next";
import { getAllPeopleApi } from "../../services/peopleService";

const TeamsPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [filterSize, setFilterSize] = useState("All");
  const [filterPerformance, setFilterPerformance] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [tableSortBy, setTableSortBy] = useState(null);
  const [tableSortOrder, setTableSortOrder] = useState("asc");
  const [showFilters, setShowFilters] = useState(false); // Added state for filter panel collapse/expand
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  // const [viewMode, setViewMode] = useState("list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const candidatesData = [
    {
      id: 1,
      name: "Alex Thompson",
      role: "Senior Software Engineer",
      department: "Engineering",
      skills: ["React", "Node.js", "Python", "AWS"],
      experience: "5 years",
      teamFit: 95,
      availability: "Available",
    },
    {
      id: 2,
      name: "Emily Rodriguez",
      role: "UX Designer",
      department: "Design",
      skills: ["Figma", "User Research", "Prototyping", "Design Systems"],
      experience: "4 years",
      teamFit: 88,
      availability: "Available",
    },
    {
      id: 3,
      name: "Mike Chen",
      role: "Data Scientist",
      department: "Analytics",
      skills: ["Python", "Machine Learning", "SQL", "Tableau"],
      experience: "6 years",
      teamFit: 92,
      availability: "Available",
    },
  ];
  // const teamSizes = ["All", "Small", "Medium", "Large"];

  const teamSizes = [
    // t("common.all"),
    // t("teamsPage.filters.smallTeam"),
    // t("teamsPage.filters.mediumTeam"),
    // t("teamsPage.filters.largeTeam"),
    { value: "All", label: t("common.all") },
    { value: "Small", label: t("teamsPage.filters.smallTeam") },
    { value: "Medium", label: t("teamsPage.filters.mediumTeam") },
    { value: "Large", label: t("teamsPage.filters.largeTeam") },
  ];
  const performanceRanges = [
    {
      value: "All",
      label: t("common.all"),
    },
    {
      value: "Excellent",
      label: t("teamsPage.filters.excellentPerformance"),
    },
    { value: "Good", label: t("teamsPage.filters.goodPerformance") },
    { value: "Average", label: t("teamsPage.filters.averagePerformance") },
  ];
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const sortOptions = [
    { value: "name", label: t("teamsPage.sort.teamName") },
    // { value: "recent", label: t("teamsPage.sort.mostRecent") },
    { value: "performance", label: t("teamsPage.sort.performance") },
    { value: "size", label: t("teamsPage.sort.teamSize") },
    {
      value: "Date Ascending",
      label: t("employees.listView.updatedAt"),
      icon: <ArrowUp size={12} />,
    },
    {
      value: "Date Descending",
      label: t("employees.listView.updatedAt"),
      icon: <ArrowDown size={12} />,
    },
  ];
  const selectedSortOption =
    sortOptions.find((option) => option.value === sortBy) || sortOptions[0];
  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const sortTeams = (teams) => {
    return [...teams].sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return new Date(b.lastUpdated) - new Date(a.lastUpdated);
        case "name":
          return a.name.localeCompare(b.name);
        case "performance":
          return b.performance - a.performance;
        case "size":
          return b.members - a.members;
        case "established":
          return new Date(b.established) - new Date(a.established);
        default:
          return 0;
      }
    });
  };

  const handleTableSort = (field) => {
    if (tableSortBy === field) {
      setTableSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setTableSortBy(field);
    setTableSortOrder("asc");
  };

  const getTableSortIcon = (field) => {
    if (tableSortBy !== field) {
      return <ChevronsUpDown size={14} className="table-sort-icon" />;
    }

    return tableSortOrder === "asc" ? (
      <ChevronUp size={14} className="table-sort-icon active" />
    ) : (
      <ChevronDown size={14} className="table-sort-icon active" />
    );
  };

  const getTeamTableSortValue = (team, field) => {
    switch (field) {
      case "teamName":
        return team.teamName || "";
      case "lead":
        return team.teamLead?.name || "";
      case "department":
        return team.department || "";
      case "updatedAt":
        return team.updatedAt ? new Date(team.updatedAt).getTime() : null;
      case "performance": {
        const performance = Number(team.teamPerformance);
        return Number.isFinite(performance) ? performance : null;
      }
      default:
        return "";
    }
  };

  const compareTeamTableValues = (aValue, bValue) => {
    const aIsEmpty = aValue === null || aValue === undefined || aValue === "";
    const bIsEmpty = bValue === null || bValue === undefined || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;

    const comparison =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: "base",
          });

    return tableSortOrder === "asc" ? comparison : -comparison;
  };

  const getPerformanceColor = (performance) => {
    if (performance >= 90) return "performance-excellent";
    if (performance >= 80) return "performance-good";
    return "performance-average";
  };

  // Smart search & filter panel (same as Employees page)
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // const getRecommendedCandidates = (team) => {
  //   return candidatesData
  //     .filter((candidate) => {
  //       const skillMatch = candidate?.skills?.some((skill) =>
  //         team.neededSkills.some(
  //           (needed) =>
  //             needed.toLowerCase().includes(skill.toLowerCase()) ||
  //             skill.toLowerCase().includes(needed.toLowerCase())
  //         )
  //       );
  //       const departmentMatch =
  //         candidate.department === team.department ||
  //         (team.department === "Engineering" &&
  //           candidate.department === "Analytics") ||
  //         (team.department === "Product" && candidate.department === "Design");
  //       return skillMatch || departmentMatch;
  //     })
  //     .slice(0, 3);
  // };

  // Count active filters for the badge (same idea as Employees page)
  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (filterSize && filterSize !== "All") count++;
    if (filterPerformance && filterPerformance !== "All") count++;
    if (sortBy && sortBy !== "name") count++;
    return count;
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterDepartment("All");
    setFilterSize("All");
    setFilterPerformance("All");
    setSortBy("name");
    setTableSortBy(null);
    setTableSortOrder("asc");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };

  const handleViewTeam = (team) => {
    console.log("Viewing team:", team);
    navigate(`/teams/${team._id}`);
  };

  const handleDeleteTeam = async (team) => {
    console.log("Deleting team:", team);
    setShowModal({
      show: true,
      type: "warning",
      title: t("teamsPage.delete.title"),
      message: t("teamsPage.delete.confirmMessage", {
        teamName: team.teamName,
      }),
      onConfirm: async () => {
        try {
          const response = await deleteTeamApi(team._id);
          if (response.code == 200) {
            getAllTeamsApiHandler();
            setShowModal({
              show: true,
              type: "success",
              showButton: false,
              title: t("common.success"),
              message: t("teamsPage.delete.successMessage", {
                teamName: team.teamName,
              }),
            });
          }
        } catch (error) {
          setShowModal({
            show: true,
            type: "error",
            showButton: false,
            title: t("common.error"),
            message: t("teamsPage.delete.failedMessage"),
          });
        }
      },
    });
  };

  const handleCreateTeam = () => {
    onNavigate("create");
  };

  useEffect(() => {
    getAllTeamsApiHandler();
  }, [
    currentPage,
    searchTerm,
    filterDepartment,
    filterPerformance,
    filterSize,
    sortBy,
    itemsPerPage,
  ]);

  useEffect(() => {
    getDepartmentsDropDownApiHandler();
    checkPeopleInOrg();
  }, []);

  const [teamData, setTeamData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamDataLength, setTeamDataLength] = useState(null);

  const sortedTableTeams = tableSortBy
    ? [...teamData].sort((a, b) =>
        compareTeamTableValues(
          getTeamTableSortValue(a, tableSortBy),
          getTeamTableSortValue(b, tableSortBy),
        ),
      )
    : teamData;
  const getAllTeamsApiHandler = async () => {
    try {
      setIsLoading(true);
      const obj = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        searchFilter: searchTerm,
        department: filterDepartment,
        performance: filterPerformance,
        teamSize: filterSize,
        sortBy: sortBy,
      };
      const response = await getAllTeamsApi(obj);
      console.log("Fetched teams data successfully");
      setTeamData(response.data);
      setTeamDataLength(response.totalCount || null);
    } catch (error) {
      console.error("Error fetching teams data:", error);
      setShowModal({
        show: true,
        type: "error",
        title: t("common.error"),
        message: t("teamsPage.errors.fetchTeamsError"),
        showButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "list";
    return window.matchMedia("(max-width: 768px)").matches ? "card" : "list";
  });
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");

    const apply = (e) => {
      if (e.matches) setViewMode("card"); // force card on mobile
    };

    // run once on mount
    apply(mq);

    // listen for resize changes
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  // CHECK THE ORG HAVE ANY PEOPLES TO CREATE A TEAM

  const [peopleCount, setPeopleCount] = useState(false);

  const checkPeopleInOrg = async () => {
    try {
      const response = await getAllPeopleApi();
      setPeopleCount(response.totalCount === 0 ? true : false);
    } catch (error) {
      console.error("Error fetching people data:", error);
    }
  };

  const start = (teamDataLength ?? 0) === 0 ? 0 : indexOfFirstItem + 1;
  const end =
    (teamDataLength ?? 0) === 0
      ? 0
      : Math.min(indexOfFirstItem + teamData.length, teamDataLength);

  const [departments, setDepartments] = useState([]);
  // GET DEPARTMENTS DROPDOWN DATA
  const getDepartmentsDropDownApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();

      // ADD "ALL" OPTION IN THE SETDEPARTMENTS DROPDOWN TO SHOW ALL DEPARTMENTS IN INTIAL LOAD
      setDepartments([{ name: t("common.all"), _id: "all" }, ...response.data]);
      console.log("Departments fetched:", departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  // Pagination logic
  const totalPages = Math.ceil((teamDataLength || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const sanitizeSearch = (val) =>
    // allow letters, numbers, space, dash, underscore, and dot; strip everything else (incl. backslash)
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");

  // skeleton loader
  // --- Skeletons (inline components) ---
  const TeamCardSkeleton = () => (
    <div className="team-card skeleton-card">
      <div className="card-header">
        <div className="skeleton skeleton-text w-40 h-5" />
        <div className="skeleton skeleton-text w-28 h-4 mt-2" />
        <div className="skeleton skeleton-text w-24 h-4 mt-2" />
        <div className="skeleton skeleton-chip w-16 h-6" />
      </div>

      <div className="team-details mt-3">
        <div className="skeleton skeleton-text w-36 h-4" />
        <div className="skeleton skeleton-text w-32 h-4 mt-2" />
      </div>

      <div className="team-description mt-3">
        <div className="skeleton skeleton-text w-full h-3" />
        <div className="skeleton skeleton-text w-5/6 h-3 mt-2" />
      </div>

      <div className="tskills-section mt-4">
        <div className="skeleton skeleton-text w-28 h-4" />
        <div className="skeleton-badges mt-2">
          <span className="skeleton skeleton-badge" />
          <span className="skeleton skeleton-badge" />
          <span className="skeleton skeleton-badge" />
          <span className="skeleton skeleton-badge" />
        </div>
      </div>

      <div className="needed-skills mt-4">
        <div className="skeleton skeleton-text w-24 h-4" />
        <div className="skeleton-badges mt-2">
          <span className="skeleton skeleton-badge" />
          <span className="skeleton skeleton-badge" />
          <span className="skeleton skeleton-badge" />
        </div>
      </div>

      <div className="card-actions mt-5">
        <div className="skeleton skeleton-btn w-28 h-9" />
      </div>
    </div>
  );

  const TeamListRowSkeleton = () => (
    <div className="team-list-row skeleton-row">
      <div className="list-col">
        <div className="skeleton skeleton-text w-40 h-4" />
      </div>
      <div className="list-col">
        <div className="skeleton skeleton-text w-32 h-4" />
      </div>
      <div className="list-col">
        <div className="skeleton skeleton-text w-24 h-4" />
      </div>
      <div className="list-col">
        <div className="skeleton skeleton-chip w-14 h-6" />
      </div>
      <div className="list-col">
        <div className="skeleton skeleton-btn w-16 h-8" />
      </div>
    </div>
  );

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (filterSize && filterSize !== "All") count++;
    if (filterPerformance && filterPerformance !== "All") count++;
    if (sortBy && sortBy !== "name") count++;
    return count;
  };

  const isTrueEmptyState =
    !isLoading && teamData.length === 0 && getActiveFiltersCount() === 0;

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <div className="teams-page">
      <main className="teams-main">
        <div className="container">
          <div className="teams-mobile-top-actions">
            <button
              className="nav-back teams-back-btn"
              onClick={() => window.history.back()}
            >
              <ArrowLeft size={16} />
              {t("common.back")}
            </button>

            <div className="teams-top-right">
              <div className="view-toggle">
                <button
                  className={`view-btn ${viewMode === "card" ? "active" : ""}`}
                  onClick={() => setViewMode("card")}
                  title={t("teamsPage.cardViewBtn")}
                >
                  <Grid size={16} />
                </button>

                <button
                  className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  title={t("teamsPage.listViewBtn")}
                >
                  <List size={16} />
                </button>
              </div>

              {!isTrueEmptyState && (
                <button
                  className="btn btn-primary create-team-btn"
                  onClick={handleCreateTeam}
                >
                  <Plus size={16} />
                  {t("teamsPage.createNewTeam")}
                </button>
              )}
            </div>
          </div>

          <div className="page-header">
            <div className="header-icon">
              <Users size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("teamsPage.pageTitle")}</h1>
              <span className="teams-title-info-wrap">
                <button
                  type="button"
                  className="teams-title-info-btn"
                  aria-label={t("teamsPage.listView.tooltip.readiness.title")}
                >
                  <Info size={18} />
                </button>

                <span className="teams-title-info-popover" role="tooltip">
                  <strong>
                    {t("teamsPage.listView.tooltip.readiness.title")}
                  </strong>
                  <span>
                    {t("teamsPage.listView.tooltip.readiness.message")}
                  </span>
                </span>
              </span>
            </div>
            <p>{t("teamsPage.pageDescription")}</p>
          </div>

          {/* Smart Search & Filter panel (same markup/classes as Employees page) */}
          <div className="smart-filter-panel-team">
            <div
              className={`smart-filter-top-bar ${showSearchBar ? "search-open" : ""}`}
            >
              {showSearchBar ? (
                <div className="smart-search-inline-row">
                  <input
                    type="text"
                    placeholder={t("teamsPage.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) =>
                      setSearchInputValue(sanitizeSearch(e.target.value))
                    }
                    className="smart-search-inline-input"
                    autoFocus
                  />
                  <span className="mobile-search-placeholder-hint">
                    {t("teamsPage.searchPlaceholder")}
                  </span>
                  <button
                    className="smart-filter-icon-btn active"
                    title={t("common.search")}
                    onClick={handleSearchButtonClick} // hide on click back
                  >
                    <Search size={18} />
                  </button>
                  {searchTerm && (
                    <button
                      className="smart-search-clear"
                      onClick={handleClearSearch}
                      title={t("common.clear")}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : null}

              {/* Icons pinned to right */}
              <div className="smart-filter-icons-right">
                {!showSearchBar && (
                  <button
                    className={`smart-filter-icon-btn ${
                      showSearchBar ? "active" : ""
                    }`}
                    onClick={() => {
                      setSearchInputValue(searchTerm);
                      setShowSearchBar(true);
                      setShowFiltersPanel(false);
                    }}
                    title={t("common.search")}
                  >
                    <Search size={18} />
                  </button>
                )}
                <div className="smart-filter-separator" />
                <button
                  className={`smart-filter-icon-btn ${
                    showFiltersPanel ? "active" : ""
                  }`}
                  onClick={() => {
                    setShowFiltersPanel(!showFiltersPanel);
                    if (!showFiltersPanel) setShowSearchBar(false);
                  }}
                  title={t("common.filters")}
                >
                  <Filter size={18} />
                </button>
              </div>

              <div className="smart-filter-info">
                {getAppliedFiltersCount() > 0 && (
                  <>
                    <span className="smart-filter-badge">
                      {getAppliedFiltersCount()}
                    </span>
                    <span>{t("common.filtersApplied")}</span>
                  </>
                )}
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("teamsPage.filters.department")}</label>
                      <select
                        value={filterDepartment}
                        onChange={(e) => setFilterDepartment(e.target.value)}
                        className={`smart-filter-select ${
                          filterDepartment !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departments.map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("teamsPage.filters.teamSize")}</label>
                      <select
                        value={filterSize}
                        onChange={(e) => setFilterSize(e.target.value)}
                        className={`smart-filter-select ${
                          filterSize !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {teamSizes.map((size) => (
                          <option key={size.value} value={size.value}>
                            {size.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("teamsPage.filters.performance")}</label>
                      <select
                        value={filterPerformance}
                        onChange={(e) => setFilterPerformance(e.target.value)}
                        className={`smart-filter-select ${
                          filterPerformance !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {performanceRanges.map((range) => (
                          <option key={range.value} value={range.value}>
                            {range.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* <div className="smart-filter-item">
                      <label>{t("teamsPage.filters.sortBy")}</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={`smart-filter-select ${
                          sortBy !== "name" ? "smart-active" : ""
                        }`}
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div> */}

                    <div className="smart-filter-item">
                      <label>{t("employees.filters.sortBy")}</label>

                      <div
                        className={`smart-sort-dropdown ${
                          sortBy !== "Name" ? "smart-active" : ""
                        }`}
                        tabIndex={0}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setShowSortDropdown(false);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="smart-sort-trigger"
                          onClick={() => setShowSortDropdown((prev) => !prev)}
                        >
                          <span className="smart-sort-value">
                            {selectedSortOption.icon && (
                              <span className="smart-sort-icon">
                                {selectedSortOption.icon}
                              </span>
                            )}
                            {selectedSortOption.label}
                          </span>

                          <ChevronDown
                            size={14}
                            className={`smart-sort-chevron ${
                              showSortDropdown ? "open" : ""
                            }`}
                          />
                        </button>

                        {showSortDropdown && (
                          <div className="smart-sort-menu">
                            {sortOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`smart-sort-option ${
                                  sortBy === option.value ? "selected" : ""
                                }`}
                                onClick={() => {
                                  setSortBy(option.value);
                                  setTableSortBy(null);
                                  setTableSortOrder("asc");
                                  setCurrentPage(1);
                                  setShowSortDropdown(false);
                                }}
                              >
                                <span>{option.label}</span>

                                {option.icon && (
                                  <span className="smart-sort-icon">
                                    {option.icon}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* **************************************************SKELTON LOADER *********************** */}

          {isLoading ? (
            // SKELETONS
            viewMode === "card" ? (
              <div className="teams-grid">
                {Array.from({ length: 6 }).map((_, i) => (
                  <TeamCardSkeleton key={`card-skel-${i}`} />
                ))}
              </div>
            ) : (
              <div className="employees-list">
                <div className="team-list-header">
                  <div
                    className="list-col table-sortable"
                    onClick={() => handleTableSort("teamName")}
                  >
                    {t("teamsPage.listView.teamName")}
                    {getTableSortIcon("teamName")}
                  </div>
                  <div
                    className="list-col table-sortable"
                    onClick={() => handleTableSort("lead")}
                  >
                    {t("teamsPage.listView.lead")}
                    {getTableSortIcon("lead")}
                  </div>
                  <div
                    className="list-col table-sortable"
                    onClick={() => handleTableSort("department")}
                  >
                    {t("teamsPage.listView.department")}
                    {getTableSortIcon("department")}
                  </div>
                  <div
                    className="list-col table-sortable"
                    onClick={() => handleTableSort("updatedAt")}
                  >
                    {t("employees.listView.updatedAt")}
                    {getTableSortIcon("updatedAt")}
                  </div>
                  <div
                    className="list-col table-sortable"
                    onClick={() => handleTableSort("performance")}
                  >
                    {t("teamsPage.listView.performance")}
                    {getTableSortIcon("performance")}
                  </div>
                  <div className="list-col">
                    {t("teamsPage.listView.actions")}
                  </div>
                </div>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TeamListRowSkeleton key={`row-skel-${i}`} />
                ))}
              </div>
            )
          ) : teamData.length === 0 ? (
            getAppliedFiltersCount() > 0 ? (
              <NoFilterData
                title={t("common.noResults")}
                message={t("common.adjustOrClear")}
                clearLabel={t("common.clearFilters")}
                onClear={clearAllFilters}
              />
            ) : viewMode === "card" ? (
              peopleCount ? (
                <EmptyState
                  icon={Users}
                  iconSize={48}
                  title={t("teamsPage.NoPeopleTitle")}
                  message={t("teamsPage.NoPeopleMessage")}
                  actionLabel={t("common.goToEmployees")}
                  onAction={() => navigate("/employees")}
                />
              ) : (
                <EmptyState
                  icon={Users}
                  iconSize={48}
                  title={t("teamsPage.emptyTitle")}
                  message={t("teamsPage.emptyMessage")}
                  actionLabel={t("teamsPage.createNewTeam")}
                  onAction={handleCreateTeam}
                />
              )
            ) : peopleCount ? (
              <EmptyState
                icon={Users}
                iconSize={48}
                title={t("teamsPage.NoPeopleTitle")}
                message={t("teamsPage.NoPeopleMessage")}
                actionLabel={t("common.goToEmployees")}
                onAction={() => navigate("/employees")}
              />
            ) : (
              <EmptyState
                icon={Users}
                iconSize={48}
                title={t("teamsPage.emptyTitle")}
                message={t("teamsPage.emptyMessage")}
                actionLabel={t("teamsPage.createNewTeam")}
                onAction={handleCreateTeam}
              />
            )
          ) : viewMode === "card" ? (
            <div className="teams-grid">
              {teamData.map((team) => {
                return (
                  <div key={team._id} className="team-card">
                    <div className="card-header">
                      <div className="team-info">
                        <h3>{team?.teamName}</h3>
                        <p className="team-department">{team?.department}</p>
                        <p className="team-lead">
                          {t("teamsPage.lead")} {team?.teamLead?.name}
                        </p>
                      </div>
                      <div
                        className={`performance-score ${getPerformanceColor(
                          team.teamPerformance,
                        )}`}
                      >
                        <TrendingUp size={14} />
                        {team?.teamPerformance
                          ? Number(team.teamPerformance)
                          : 0}
                        %
                      </div>
                    </div>

                    <div className="team-details">
                      <div className="detail-row">
                        <Users size={14} />
                        <span>
                          {t("teamsPage.members", { count: team.membersCount })}
                        </span>
                      </div>
                      <div className="detail-row">
                        <Plus size={14} />
                        <span>
                          {t("teamsPage.openPositions", {
                            count: team.openPositions,
                          })}
                        </span>
                      </div>
                      <div className="detail-row">
                        <Calendar size={14} />
                        <span>{formatEmployeeCreatedAt(team.updatedAt)}</span>
                      </div>
                    </div>

                    <div className="team-description">
                      <p>{team?.description}</p>
                    </div>

                    <div className="tskills-section">
                      <h4>{t("teamsPage.keySkills")}</h4>
                      <div className="tskills-tags">
                        {team?.teamSkills ? (
                          <>
                            {team?.teamSkills
                              ?.slice(0, 4)
                              .map((skill, index) => (
                                <span key={index} className="skill-tag">
                                  {skill}
                                </span>
                              ))}
                          </>
                        ) : (
                          <span className="no-skills">
                            {t("teamsPage.noSkillsFound")}
                          </span>
                        )}
                        {team?.teamSkills?.length > 4 && (
                          <span className="tskill-tag more">
                            +{team?.teamSkills?.length - 4}{" "}
                            {t("teamsPage.more")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="needed-skills">
                      <h4>{t("teamsPage.lookingFor")}</h4>
                      <div className="needed-tags">
                        {team?.neededSkills ? (
                          team?.neededSkills
                            ?.slice(0, 4)
                            .map((skill, index) => (
                              <span key={index} className="needed-tag">
                                {skill}
                              </span>
                            ))
                        ) : (
                          <span className="no-skills">
                            {t("common.noDataFound")}
                          </span>
                        )}
                        {team?.neededSkills?.length > 4 && (
                          <span className="needed-tag more">
                            +{team.neededSkills.length - 4}{" "}
                            {t("teamsPage.more")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="card-actions team-card-actions">
                      <button
                        className="btn btn-primary team-card-view-btn"
                        onClick={() => handleViewTeam(team)}
                      >
                        <Eye size={16} />
                        {t("teamsPage.viewTeamDetails")}
                      </button>

                      <button
                        className="btn team-card-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTeam(team);
                        }}
                      >
                        <Trash2 size={16} />
                        {t("common.delete")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="employees-list">
              <div className="team-list-header">
                <div
                  className="list-col table-sortable"
                  onClick={() => handleTableSort("teamName")}
                >
                  {t("teamsPage.listView.teamName")}
                  {getTableSortIcon("teamName")}
                </div>
                <div
                  className="list-col table-sortable"
                  onClick={() => handleTableSort("lead")}
                >
                  {t("teamsPage.listView.lead")}
                  {getTableSortIcon("lead")}
                </div>
                <div
                  className="list-col table-sortable"
                  onClick={() => handleTableSort("department")}
                >
                  {t("teamsPage.listView.department")}
                  {getTableSortIcon("department")}
                </div>
                <div
                  className="list-col table-sortable"
                  onClick={() => handleTableSort("updatedAt")}
                >
                  {t("employees.listView.updatedAt")}
                  {getTableSortIcon("updatedAt")}
                </div>
                <div
                  className="list-col table-sortable"
                  style={{ display: "inline-flex", alignItems: "center" }}
                  onClick={() => handleTableSort("performance")}
                >
                  {t("teamsPage.listView.performance")}
                  <CustomTooltip
                    tooltip={{
                      title: t("teamsPage.listView.tooltip.performanceTitle"),
                      message: t(
                        "teamsPage.listView.tooltip.performanceMessage",
                      ),
                    }}
                    id="performance-tooltip"
                  >
                    <Info
                      size={14}
                      color="#888"
                      style={{
                        marginLeft: "6px",
                        cursor: "pointer",
                        verticalAlign: "middle",
                      }}
                    />
                  </CustomTooltip>
                  {getTableSortIcon("performance")}
                </div>
                <div className="list-col">
                  {t("teamsPage.listView.actions")}
                </div>
              </div>
              {sortedTableTeams.map((team) => (
                <div
                  key={team._id}
                  className="team-list-row"
                  onClick={() => handleViewTeam(team)}
                >
                  <div className="list-col">{team.teamName}</div>
                  <div className="list-col">{team.teamLead?.name}</div>
                  <div className="list-col">{team.department}</div>
                  <div className="list-col">
                    {formatEmployeeCreatedAt(team.updatedAt)}
                  </div>

                  <div className="list-col">
                    <span
                      className={`performance-badge ${getPerformanceColor(
                        team.teamPerformance ? team.teamPerformance : 0,
                      )}`}
                    >
                      {team.teamPerformance == null
                        ? 0
                        : Number(team.teamPerformance)}
                      %
                    </span>
                  </div>
                  <div className="list-col">
                    <button
                      style={{
                        paddingRight: "1rem",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTeam(team);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      className="btn "
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewTeam(team);
                      }}
                    >
                      <Eye size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && teamDataLength > 0 && (
            <Pagination
              loading={isLoading}
              total={teamDataLength}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={teamData.length}
              onPageChange={setCurrentPage}
              pageSizeOptions={[20, 40, 60, 100]}
              onPageSizeChange={(size) => {
                //change itemsPerPage:
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((teamDataLength || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("teamsPage.paginationInfo", {
                  start,
                  end,
                  total,
                })
              }
            />
          )}
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({ show: false, type: "", title: "", message: "" })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={showModal.showButton}
        onConfirm={showModal.onConfirm}
      >
        <div actionLabel={t("teamsPage.delete.actionLabel")} />
      </Modal>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default TeamsPage;
